/**
 * plp.js — PLP filter + sort + load-more (AJAX, no full reload)
 *
 *  1. Filter/sort change → fetch the filtered URL, swap only #product-grid in
 *     place, pushState the URL. Scroll position is preserved (no jump to top).
 *  2. Load More → fetch the next page, append its product cards to the grid,
 *     focus the first new card. Stays in place.
 *  3. Active-filter indicators (incl. price range) → red label + count badge on
 *     each facet, removable pills row, dynamic "Clear all". Driven client-side
 *     from the desktop form so dropdowns stay open and nothing scrolls.
 *  4. Mobile filter drawer with focus trap.
 *
 * Progressive enhancement: the Load More button and filter form are real links/
 * inputs; with JS off the page still works via normal navigation.
 *
 * Loaded as <script type="module" defer>.
 */

const GRID_ID = 'product-grid';
const desktopForm = () => document.querySelector('#FacetFiltersForm');
const mobileForm  = () => document.querySelector('#MobileFacetFiltersForm');
const gridRoot    = () => document.getElementById(GRID_ID);

// ─── URL building ────────────────────────────────────────────────────────────

/** Build a URL from current location, replacing filter/page params with the form's. */
function buildUrl(form, extraParams) {
  const url = new URL(window.location.href);
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key);
  }
  if (form) {
    for (const [key, value] of new FormData(form).entries()) {
      if (value !== '') url.searchParams.append(key, value);
    }
  }
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      if (value !== '' && value != null) url.searchParams.set(key, String(value));
      else url.searchParams.delete(key);
    }
  }
  return url;
}

// ─── AJAX fetch + swap ───────────────────────────────────────────────────────

let inFlight = null;

async function fetchDoc(url) {
  const res = await fetch(url.toString(), { headers: { 'X-Requested-With': 'fetch' } });
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  return new DOMParser().parseFromString(await res.text(), 'text/html');
}

/** Filter/sort change: replace the grid in place, keep scroll, update URL + indicators. */
async function applyFilters(url, { push = true } = {}) {
  const grid = gridRoot();
  if (!grid) { window.location.href = url.toString(); return; }

  grid.classList.add('loading');
  grid.setAttribute('aria-busy', 'true');
  const token = (inFlight = {});

  try {
    const doc = await fetchDoc(url);
    if (token !== inFlight) return; // a newer request superseded this one
    const fresh = doc.getElementById(GRID_ID);
    if (!fresh) throw new Error('No #product-grid in response');
    grid.innerHTML = fresh.innerHTML;
    // The swapped-in grid is brand-new DOM: without this the reveal observer has
    // never seen it, so every card stays at opacity 0 and the PLP looks empty.
    window.GrittReveal?.refresh(grid);
    if (push) history.pushState({ plp: true }, '', url.toString());
    refreshIndicators();
    announceCount();
  } catch (err) {
    // Graceful fallback to a normal navigation.
    window.location.href = url.toString();
    return;
  } finally {
    if (token === inFlight) {
      grid.classList.remove('loading');
      grid.removeAttribute('aria-busy');
    }
  }
}

/** Load More: append next page's cards, focus the first new one. */
async function loadMore(btn) {
  const nextUrl = btn.getAttribute('href');
  const list = document.querySelector('[data-product-list]');
  const wrap = btn.closest('[data-loadmore-wrap]');
  if (!nextUrl || !list) return;

  wrap?.classList.add('is-loading');
  btn.setAttribute('aria-busy', 'true');

  // Swap the label for the duration ("Load More Products" → "Loading products"),
  // so the wait is stated, not just animated — the racing stripes are decoration
  // and are switched off under prefers-reduced-motion.
  const labelEl = btn.querySelector('[data-load-more-label]');
  const restLabel = labelEl?.textContent;
  const loadingLabel = btn.getAttribute('data-loading-label');
  if (labelEl && loadingLabel) labelEl.textContent = loadingLabel;

  const started = Date.now();

  try {
    const doc = await fetchDoc(new URL(nextUrl, window.location.origin));
    // Hold the racing state for a beat if the fetch came back faster than the eye
    // can read it — a 90ms flash of stripes looks like a glitch, not a load.
    const shortfall = 400 - (Date.now() - started);
    if (shortfall > 0) await new Promise((r) => setTimeout(r, shortfall));
    const newItems = [...doc.querySelectorAll('[data-product-list] > .plp-grid__item')];
    const firstNew = newItems[0] || null;
    newItems.forEach((item) => list.appendChild(item));
    // The list's stagger group already fired and was unobserved, so these have to
    // be revealed directly — otherwise they load invisibly and read as "nothing
    // happened" (client, 21 Aug 2026).
    window.GrittReveal?.refresh(newItems);

    // Swap in the next button (or remove the wrap when there are no more pages).
    const nextBtn = doc.querySelector('[data-load-more]');
    if (nextBtn && wrap) {
      btn.setAttribute('href', nextBtn.getAttribute('href'));
    } else {
      wrap?.remove();
    }

    announceCount();
    // Stay in place; move focus to the first newly loaded card for keyboard/SR users.
    if (firstNew) {
      const focusable = firstNew.querySelector('a, button');
      if (focusable) {
        focusable.focus({ preventScroll: true });
      } else {
        firstNew.setAttribute('tabindex', '-1');
        firstNew.focus({ preventScroll: true });
      }
    }
  } catch (err) {
    window.location.href = nextUrl; // fallback
  } finally {
    wrap?.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    if (labelEl && restLabel != null) labelEl.textContent = restLabel;
  }
}

// ─── Active-filter indicators (client-side, from the desktop form) ────────────

function priceActive(form) {
  const inputs = form.querySelectorAll('[data-filter-price]');
  return [...inputs].some((i) => i.value !== '');
}

/** Recompute per-facet active state, badges, pills, clear-all, mobile badge. */
function refreshIndicators() {
  const form = desktopForm();
  if (!form) return;

  const pills = [];
  let totalActive = 0;

  form.querySelectorAll('.plp-filters__group').forEach((group) => {
    const summary = group.querySelector('.plp-filters__summary');
    if (!summary) return;
    const isPrice = group.dataset.filterType === 'price_range';

    let count = 0;
    if (isPrice) {
      if (priceActive(group)) {
        count = 1;
        const min = group.querySelector('[name$=".gte"]')?.value;
        const max = group.querySelector('[name$=".lte"]')?.value;
        pills.push({
          label: `Price: ${min ? '$' + min : '$0'} – ${max ? '$' + max : '∞'}`,
          clear: () => group.querySelectorAll('[data-filter-price]').forEach((i) => (i.value = '')),
        });
      }
    } else {
      group.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        count++;
        const text = cb.closest('label')?.querySelector('.plp-filters__label-text')?.textContent.trim() || cb.value;
        pills.push({ label: text, clear: () => { cb.checked = false; } });
      });
    }

    totalActive += count;
    group.classList.toggle('is-active', count > 0);

    // Manage the count badge inside the summary (create/update/remove).
    let badge = summary.querySelector('.plp-filters__active-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'plp-filters__active-count';
        summary.insertBefore(badge, summary.querySelector('svg'));
      }
      badge.textContent = String(count);
      badge.setAttribute('aria-label', `${count} selected`);
    } else if (badge) {
      badge.remove();
    }
  });

  renderPills(pills);

  const clearAll = form.querySelector('[data-clear-all]');
  if (clearAll) clearAll.toggleAttribute('hidden', totalActive === 0);

  updateMobileBadge(totalActive);
}

function renderPills(pills) {
  const container = document.querySelector('[id^="active-filter-pills"]');
  if (!container) return;
  container.innerHTML = '';
  pills.forEach(({ label, clear }) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'plp-filters__pill';
    btn.setAttribute('aria-label', `Remove filter: ${label}`);
    btn.innerHTML = `<span>${label}</span><svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2 2L8 8M8 2L2 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
    btn.addEventListener('click', () => {
      clear();
      applyFilters(buildUrl(desktopForm(), { sort_by: sortValue() }));
    });
    container.appendChild(btn);
  });
}

function updateMobileBadge(total) {
  const badge = document.querySelector('[data-filter-count]');
  if (!badge) return;
  badge.textContent = String(total);
  badge.dataset.count = String(total);
  badge.style.display = total > 0 ? 'inline-flex' : 'none';
}

function announceCount() {
  const status = document.querySelector('.plp-grid__count');
  const shown = document.querySelectorAll('[data-product-list] > .plp-grid__item').length;
  if (status) status.textContent = `${shown} products shown`;
}

function sortValue() {
  return document.querySelector('[data-sort-select]')?.value ?? null;
}

// ─── Delegated events (survive grid innerHTML swaps) ──────────────────────────

document.addEventListener('change', (e) => {
  const t = e.target;
  if (t.matches('#FacetFiltersForm [data-filter-input]')) {
    applyFilters(buildUrl(desktopForm(), { sort_by: sortValue() }));
  } else if (t.matches('[data-sort-select]')) {
    applyFilters(buildUrl(desktopForm(), { sort_by: t.value }));
  }
});

document.addEventListener('click', (e) => {
  const loadBtn = e.target.closest('[data-load-more]');
  if (loadBtn) { e.preventDefault(); loadMore(loadBtn); return; }

  const clearAll = e.target.closest('[data-clear-all]');
  if (clearAll) {
    e.preventDefault();
    const form = desktopForm();
    form?.querySelectorAll('input[type="checkbox"]').forEach((cb) => (cb.checked = false));
    form?.querySelectorAll('[data-filter-price]').forEach((i) => (i.value = ''));
    applyFilters(buildUrl(form, { sort_by: sortValue() }));
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && e.target.matches('#FacetFiltersForm [data-filter-price]')) {
    e.preventDefault();
    applyFilters(buildUrl(desktopForm(), { sort_by: sortValue() }));
  }
});

// Back/forward: re-sync grid to the URL without scrolling.
window.addEventListener('popstate', () => {
  applyFilters(new URL(window.location.href), { push: false });
  syncFormFromUrl(desktopForm());
});

// ─── Mobile filter drawer (focus trap) ───────────────────────────────────────

let focusTrapBefore = null;
const drawerEls = () => ({
  open: document.querySelector('[data-filter-open]'),
  drawer: document.querySelector('[data-filter-drawer]'),
  backdrop: document.querySelector('[data-filter-backdrop]'),
  close: document.querySelector('[data-filter-close]'),
  apply: document.querySelector('[data-filter-apply]'),
});

function getFocusable(c) {
  return [...c.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')]
    .filter((el) => !el.closest('[aria-hidden="true"]') && el.offsetParent !== null);
}

function openDrawer() {
  const { drawer, backdrop, open, close } = drawerEls();
  if (!drawer || !backdrop) return;
  focusTrapBefore = document.activeElement;
  drawer.setAttribute('aria-hidden', 'false');
  drawer.classList.add('is-open');
  backdrop.classList.add('is-open');
  backdrop.setAttribute('aria-hidden', 'false');
  open?.setAttribute('aria-expanded', 'true');
  close?.focus();
  document.body.style.overflow = 'hidden';
  syncFormFromUrl(mobileForm());
  document.addEventListener('keydown', drawerKeydown);
}

function closeDrawer() {
  const { drawer, backdrop, open } = drawerEls();
  if (!drawer || !backdrop) return;
  drawer.setAttribute('aria-hidden', 'true');
  drawer.classList.remove('is-open');
  backdrop.classList.remove('is-open');
  backdrop.setAttribute('aria-hidden', 'true');
  open?.setAttribute('aria-expanded', 'false');
  document.body.style.overflow = '';
  document.removeEventListener('keydown', drawerKeydown);
  focusTrapBefore?.focus();
  focusTrapBefore = null;
}

function drawerKeydown(e) {
  const { drawer } = drawerEls();
  if (!drawer) return;
  if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
  if (e.key !== 'Tab') return;
  const f = getFocusable(drawer);
  if (!f.length) return;
  const first = f[0], last = f[f.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

document.addEventListener('click', (e) => {
  if (e.target.closest('[data-filter-open]')) openDrawer();
  else if (e.target.closest('[data-filter-close]') || e.target.closest('[data-filter-backdrop]')) closeDrawer();
  else if (e.target.closest('[data-filter-apply]')) {
    e.preventDefault();
    const url = buildUrl(mobileForm(), { sort_by: sortValue() });
    closeDrawer();
    applyFilters(url);
  }
});

// ─── Form ↔ URL sync ─────────────────────────────────────────────────────────

function syncFormFromUrl(form) {
  if (!form) return;
  const params = new URLSearchParams(window.location.search);
  form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
    cb.checked = params.getAll(cb.name).includes(cb.value);
  });
  form.querySelectorAll('input[type="number"][data-filter-price]').forEach((i) => {
    const v = params.get(i.name);
    if (v != null) i.value = v;
  });
}

// ─── Init ────────────────────────────────────────────────────────────────────

refreshIndicators();

window.addEventListener('pageshow', (e) => {
  if (e.persisted) { gridRoot()?.classList.remove('loading'); refreshIndicators(); }
});
